import {
  CUSTOM_ARTICLE_CATEGORIES,
  type CustomArticle,
  HELP_CATEGORIES,
  type HelpCategory,
  helpSlugify,
  type Permission,
  validateCustomArticle,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { BookOpen, Pencil, Plus, Trash2 } from 'lucide-react';
import { useId, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { Markdown } from '../components/help/markdown';
import { PageHeader } from '../components/layout/app-shell';
import { EditorError, Panel, SelectRow, TextRow } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { articleBySlug, isBuiltInSlug } from '../lib/help/articles';
import { customArticlesQuery } from '../lib/help/custom';

// Admin → Help articles (task 27a, PLAN §12.6): program-specific articles written in the app with a Markdown editor,
// shown and searched alongside the built-in ones; and what readers are missing (least helpful articles, searches
// that found nothing).

export const Route = createFileRoute('/admin/help')({ component: HelpArticlesScreen });

const TOUR = {
  id: 'admin-help',
  steps: [
    {
      target: '[data-tour="help-articles"]',
      title: 'Your own articles',
      body: 'Write your methodology, sheet-writing standards or house rules. They appear in Onboarding and in search next to the built-in guides.',
      learnMore: 'write-help-articles',
    },
    {
      target: '[data-tour="help-insights"]',
      title: 'What readers are missing',
      body: 'Articles people found unhelpful, with their comments, and searches that found nothing: good candidates for a new article.',
    },
  ],
};

const ADMIN_KEY = ['admin', 'help', 'articles'];
const INSIGHTS_KEY = ['admin', 'help', 'insights'];

const READERS: { value: '' | Permission; label: string }[] = [
  { value: '', label: 'Everyone' },
  { value: 'content.write', label: 'People who upload and edit' },
  { value: 'content.review', label: 'Reviewers and admins' },
  { value: 'admin.manage', label: 'Admins only' },
];
const readersLabel = (a: Pick<CustomArticle, 'permission' | 'category'>) =>
  a.category === 'admin' ? 'Admins only' : (READERS.find(r => r.value === (a.permission ?? ''))?.label ?? 'Everyone');
const categoryLabel = (k: HelpCategory) => HELP_CATEGORIES.find(c => c.key === k)?.label ?? k;
const when = (ms: number) => new Date(ms).toLocaleDateString(undefined, { dateStyle: 'medium' });

interface Insights {
  leastHelpful: { slug: string; yes: number; no: number; comments: { text: string; at: number }[] }[];
  misses: { query: string; count: number; lastAt: number }[];
}

function HelpArticlesScreen() {
  useScreenTour(TOUR);
  const q = useQuery({ queryKey: ADMIN_KEY, queryFn: () => api<{ articles: CustomArticle[] }>('/admin/help/articles') });
  const [editing, setEditing] = useState<CustomArticle | 'new' | null>(null);

  if (q.isError)
    return (
      <>
        <PageHeader title="Help articles" />
        <ErrorState
          error={q.error}
          title="The help articles couldn’t be loaded"
          onRetry={() => q.refetch()}
          learnMore="write-help-articles"
        />
      </>
    );
  const articles = q.data?.articles;
  return (
    <div className="space-y-6">
      <PageHeader
        title="Help articles"
        description="Your own articles in Onboarding — methodology, sheet-writing standards, house rules — and what readers are missing."
        actions={
          !editing && (
            <Button onClick={() => setEditing('new')}>
              <Plus aria-hidden="true" /> New article
            </Button>
          )
        }
      />
      <Tip id="admin-help-articles" learnMore="write-help-articles">
        Articles are written in Markdown: <code>## Heading</code>, <code>**bold**</code>, lists and tables. Link another article with{' '}
        <code>[its title](/onboarding/its-address)</code>.
      </Tip>
      {editing ? (
        <ArticleEditor
          key={editing === 'new' ? 'new' : editing.id}
          article={editing === 'new' ? null : editing}
          taken={(articles ?? []).filter(a => editing === 'new' || a.id !== editing.id).map(a => a.slug)}
          onDone={() => setEditing(null)}
        />
      ) : (
        <section aria-labelledby="custom-title" className="space-y-3" data-tour="help-articles">
          <h2 id="custom-title" className="font-semibold">
            Your articles
          </h2>
          {!articles ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : articles.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No articles of your own yet"
              action={
                <Button variant="outline" onClick={() => setEditing('new')}>
                  <Plus aria-hidden="true" /> Write the first one
                </Button>
              }
              learnMore="write-help-articles"
            >
              Add what's specific to your programme, such as how you grade sources or how profile sheets should be written.
            </EmptyState>
          ) : (
            <ul className="divide-y rounded-lg border bg-surface" aria-label="Your articles">
              {articles.map(a => (
                <li key={a.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm" aria-label={a.title}>
                  <div className="min-w-0 flex-1">
                    <Link to="/onboarding/$slug" params={{ slug: a.slug }} className="font-medium underline-offset-2 hover:underline">
                      <DataText>{a.title}</DataText>
                    </Link>
                    <span className="block text-xs text-muted-foreground">
                      {categoryLabel(a.category)} · {readersLabel(a)} · updated {when(a.updatedAt)}
                      {a.updatedByName && (
                        <>
                          {' '}
                          by <DataText>{a.updatedByName}</DataText>
                        </>
                      )}
                    </span>
                    {isBuiltInSlug(a.slug) && (
                      <span role="status" className="block text-xs text-warning-foreground">
                        Hidden: a built-in article now uses the address “{a.slug}”. Change this article's address.
                      </span>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setEditing(a)}>
                    <Pencil aria-hidden="true" /> Edit<span className="sr-only"> {a.title}</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      <HelpInsights />
    </div>
  );
}

function ArticleEditor({ article, taken, onDone }: { article: CustomArticle | null; taken: string[]; onDone: () => void }) {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useConfirm();
  const [title, setTitle] = useState(article?.title ?? '');
  const [slug, setSlug] = useState(article?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(!!article);
  const [category, setCategory] = useState<HelpCategory>(article?.category ?? 'core-concepts');
  const [readers, setReaders] = useState<'' | Permission>(article?.permission ?? '');
  const [summary, setSummary] = useState(article?.summary ?? '');
  const [tags, setTags] = useState(article?.tags.join(', ') ?? '');
  const [related, setRelated] = useState(article?.related.join(', ') ?? '');
  const [body, setBody] = useState(article?.body ?? '');
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [error, setError] = useState<string | null>(null);
  const bodyId = useId();

  const address = slugTouched ? slug : helpSlugify(title);
  const draft = {
    title,
    slug: address,
    category,
    permission: category === 'admin' ? null : readers || null,
    summary,
    tags: tags.split(','),
    related: related.split(','),
    body,
  };
  const clash = isBuiltInSlug(address)
    ? 'A built-in article uses this address; choose another.'
    : taken.includes(address)
      ? 'Another of your articles uses this address.'
      : null;
  const unknownRelated = draft.related.map(s => s.trim()).filter(s => s && !articleBySlug(s) && s !== 'glossary' && !taken.includes(s));

  const done = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ADMIN_KEY }), qc.invalidateQueries({ queryKey: customArticlesQuery.queryKey })]);
    onDone();
  };
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
  };
  const save = useMutation({
    mutationFn: () =>
      withReauth(() =>
        article
          ? api(`/admin/help/articles/${article.id}`, { method: 'PUT', body: draft })
          : api('/admin/help/articles', { method: 'POST', body: draft }),
      ),
    onSuccess: done,
    onError,
  });
  const remove = useMutation({
    mutationFn: () => withReauth(() => api(`/admin/help/articles/${article?.id}`, { method: 'DELETE' })),
    onSuccess: done,
    onError,
  });

  const submit = () => {
    setError(null);
    const v = validateCustomArticle(draft);
    if (!v.ok) return setError(v.errors.join(' '));
    if (clash) return setError(clash);
    save.mutate();
  };

  return (
    <Panel
      title={article ? `Edit “${article.title}”` : 'New article'}
      actions={
        article && (
          <Button
            size="sm"
            variant="ghost"
            disabled={remove.isPending}
            onClick={async () => {
              if (
                await confirm({
                  title: 'Delete this article?',
                  body: 'It disappears from Onboarding and search for everyone. Links to it will show “Article not found”.',
                  confirmLabel: 'Delete article',
                  destructive: true,
                })
              )
                remove.mutate();
            }}
          >
            <Trash2 aria-hidden="true" /> Delete
          </Button>
        )
      }
    >
      <form
        className="space-y-4"
        onSubmit={e => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextRow label="Title" value={title} onChange={setTitle} required placeholder="e.g. Our research methodology" />
          <div className="space-y-1">
            <TextRow
              label="Address"
              value={address}
              onChange={v => {
                setSlugTouched(true);
                setSlug(v.toLowerCase().replace(/\s+/g, '-'));
              }}
              mono
              help="The last part of the article's link: lowercase letters, digits and dashes. Filled in from the title until you change it."
            />
            <p className="text-xs text-muted-foreground">
              Link: <span className="font-mono">/onboarding/{address || '…'}</span>
            </p>
            {clash && (
              <p role="status" className="text-xs text-warning-foreground">
                {clash}
              </p>
            )}
          </div>
          <SelectRow
            label="Section"
            value={category}
            onChange={setCategory}
            options={CUSTOM_ARTICLE_CATEGORIES.map(c => ({ value: c.key, label: c.label }))}
            help="Where the article appears in the Onboarding topic tree."
            learnMore="write-help-articles"
          />
          <SelectRow
            label="Who can read it"
            value={category === 'admin' ? 'admin.manage' : readers}
            onChange={setReaders}
            options={READERS}
            disabled={category === 'admin'}
            help="Admin guides are always for admins only. Guides for editing can be hidden from viewers."
            learnMore="write-help-articles"
          />
          <TextRow label="Summary" value={summary} onChange={setSummary} placeholder="One sentence under the title" />
          <TextRow
            label="Tags"
            value={tags}
            onChange={setTags}
            placeholder="e.g. methodology, sources"
            help="Comma-separated words that help search find the article."
          />
          <div className="space-y-1 md:col-span-2">
            <TextRow
              label="Related articles"
              value={related}
              onChange={setRelated}
              mono
              placeholder="e.g. reliability-grading, core-concepts"
              help="Addresses of articles to list under “Related”, comma-separated."
            />
            {unknownRelated.length > 0 && (
              <p role="status" className="text-xs text-warning-foreground">
                No article at: {unknownRelated.join(', ')}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={bodyId} className="text-sm font-medium">
              Article text (Markdown)
              <span className="text-danger-foreground" aria-hidden="true">
                {' '}
                *
              </span>
            </label>
            <div role="tablist" aria-label="Editor view" className="flex gap-1 rounded-md border p-0.5">
              {(['write', 'preview'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  aria-controls={`${bodyId}-${t}`}
                  onClick={() => setTab(t)}
                  className="rounded px-2.5 py-1 text-xs aria-selected:bg-muted aria-selected:font-medium"
                >
                  {t === 'write' ? 'Write' : 'Preview'}
                </button>
              ))}
            </div>
          </div>
          <div id={`${bodyId}-write`} role="tabpanel" hidden={tab !== 'write'}>
            <textarea
              id={bodyId}
              dir="auto"
              value={body}
              onChange={e => setBody(e.target.value)}
              aria-required
              placeholder={'## First section\n\nWrite in plain sentences. Use lists for steps:\n\n1. Open …\n2. Choose …'}
              className="min-h-80 w-full rounded-md border border-input bg-surface px-3 py-2 font-mono text-sm placeholder:text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground">
              <DataNumber>{body.length.toLocaleString()}</DataNumber> characters. Images from outside Hoopoe aren't shown.
            </p>
          </div>
          <div
            id={`${bodyId}-preview`}
            role="tabpanel"
            aria-label="Preview"
            hidden={tab !== 'preview'}
            className="min-h-80 rounded-md border bg-surface-raised p-4"
          >
            {title && <h1 className="mb-1 text-2xl font-semibold tracking-tight">{title}</h1>}
            {summary && <p className="mb-3 text-sm text-muted-foreground">{summary}</p>}
            {body.trim() ? <Markdown source={body} dir="auto" /> : <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>}
          </div>
        </div>

        <EditorError error={error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : article ? 'Save changes' : 'Publish article'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function HelpInsights() {
  const q = useQuery({ queryKey: INSIGHTS_KEY, queryFn: () => api<Insights>('/admin/help/insights') });
  const d = q.data;
  return (
    <section aria-labelledby="insights-title" className="space-y-4" data-tour="help-insights">
      <h2 id="insights-title" className="font-semibold">
        What readers are missing
      </h2>
      {q.isError && <ErrorState error={q.error} title="Feedback couldn’t be loaded" onRetry={() => q.refetch()} />}
      {!d && !q.isError && <p className="text-sm text-muted-foreground">Loading…</p>}
      {d && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 rounded-lg border bg-surface p-4 text-sm">
            <h3 className="font-medium">Least helpful articles</h3>
            {d.leastHelpful.length === 0 ? (
              <p className="text-muted-foreground">Nobody has answered “No” to “Was this helpful?” yet.</p>
            ) : (
              <ul className="space-y-2" aria-label="Least helpful articles">
                {d.leastHelpful.map(a => (
                  <li key={a.slug}>
                    <Link to="/onboarding/$slug" params={{ slug: a.slug }} className="font-medium underline-offset-2 hover:underline">
                      {a.slug === 'glossary' ? 'Glossary' : (articleBySlug(a.slug)?.title ?? a.slug)}
                    </Link>{' '}
                    <span className="text-muted-foreground">
                      <DataNumber>{a.no}</DataNumber> no · <DataNumber>{a.yes}</DataNumber> yes
                    </span>
                    {a.comments.length > 0 && (
                      <ul className="mt-1 space-y-0.5 border-s ps-3 text-xs text-muted-foreground">
                        {a.comments.slice(0, 3).map(c => (
                          <li key={c.at}>
                            “<DataText>{c.text}</DataText>”
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="space-y-2 rounded-lg border bg-surface p-4 text-sm">
            <h3 className="font-medium">Searches that found nothing</h3>
            {d.misses.length === 0 ? (
              <p className="text-muted-foreground">Every help search so far found something.</p>
            ) : (
              <ul className="divide-y" aria-label="Searches that found nothing">
                {d.misses.map(m => (
                  <li key={m.query} className="flex items-center gap-2 py-1">
                    <DataText className="min-w-0 flex-1 truncate">{m.query}</DataText>
                    <DataNumber className="text-xs text-muted-foreground">{m.count}×</DataNumber>
                    <span className="text-xs text-muted-foreground">{when(m.lastAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <LearnMore slug="write-help-articles" />
    </section>
  );
}
